// [Proposition: roles-projet] The four project roles, from the most to the
// least involved person. Names and explanations live in the i18n dictionary
// (dicts/proposals.ts, keys "roles.*") — renaming or redefining a role means
// editing those texts only. The PMO accompanist (always an admin) and the
// Sponsor are each set by the project's own field; Contributor and Consulted
// are picked per team member, stored as scoping.allocations[].project_role
// (the former free-text "role" becomes the member's "précision").
// Server twin: src/server/proposals/projectRoles.ts (keep ids in sync).

export type ProjectRoleId = 'accompanist' | 'sponsor' | 'contributor' | 'consulted';

export interface ProjectRoleDef {
  id: ProjectRoleId;
  labelKey: string;
  descKey: string;
}

// Static keys so the i18n checker can resolve them.
export const PROJECT_ROLES: ProjectRoleDef[] = [
  { id: 'accompanist', labelKey: 'roles.accompanist', descKey: 'roles.accompanist.desc' },
  { id: 'sponsor', labelKey: 'roles.sponsor', descKey: 'roles.sponsor.desc' },
  { id: 'contributor', labelKey: 'roles.contributor', descKey: 'roles.contributor.desc' },
  { id: 'consulted', labelKey: 'roles.consulted', descKey: 'roles.consulted.desc' },
];

export function getProjectRole(id: string | null | undefined): ProjectRoleDef | null {
  return PROJECT_ROLES.find(r => r.id === id) ?? null;
}

/** Roles that can be picked for a team member — not the PMO accompanist nor the Sponsor: each comes from its own project field. */
export const ASSIGNABLE_ROLES: ProjectRoleDef[] = PROJECT_ROLES.filter(r => r.id !== 'sponsor' && r.id !== 'accompanist');

function sameName(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a?.trim() && !!b?.trim() && a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * A member's role as displayed: Sponsor when they're the project's sponsor
 * (the Sponsor field is the one place the sponsor is defined), otherwise
 * their picked role — a stale 'sponsor' pick on someone else counts as unset.
 */
export function effectiveProjectRole(name: string, storedRole: string | null | undefined, sponsorName: string | null | undefined): string {
  if (sameName(name, sponsorName)) return 'sponsor';
  return storedRole && ASSIGNABLE_ROLES.some(r => r.id === storedRole) ? storedRole : '';
}

/** Role given to someone newly added to the team: none for the sponsor (derived), else Contributor. */
export function defaultProjectRole(name: string, sponsorName: string | null | undefined): string {
  return sameName(name, sponsorName) ? '' : 'contributor';
}

/**
 * Display text for a member's role and précision — "Contributeur·rice — négociation",
 * just the role, or just the précision for members without a role yet — and
 * the role's explanation for a tooltip.
 */
export function roleDisplay(
  t: (key: string, params?: Record<string, string | number>) => string,
  projectRole: string | null | undefined,
  precision: string | null | undefined,
): { text: string; title: string | undefined } {
  const role = getProjectRole(projectRole);
  const p = precision?.trim() || '';
  const label = role ? t(role.labelKey) : '';
  const text = label && p ? `${label} — ${p}` : label || p;
  return { text, title: role ? `${label} — ${t(role.descKey)}` : undefined };
}
